import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import BillingSummary from "../components/BillingSummary";
import { fetchUsers } from "../api/userAPI";

function YooChatPage() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [roomUrl, setRoomUrl] = useState(null);
  const [isStartingSession, setIsStartingSession] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [error, setError] = useState(null);
  const [hoverUser, setHoverUser] = useState(null);
  const [isAddingTestUser, setIsAddingTestUser] = useState(false);

  // Fetch users from API on component mount
  useEffect(() => {
    const getUsers = async () => {
      try {
        setIsLoadingUsers(true);
        setError(null);
        const userData = await fetchUsers();
        setUsers(userData);
      } catch (err) {
        console.error("Error fetching users:", err);
        setError("Failed to load users. Please try again later.");
      } finally {
        setIsLoadingUsers(false);
      }
    };

    getUsers();
  }, []);

  // Add a test user to the database
  const addTestUser = async () => {
    try {
      setIsAddingTestUser(true);
      setError(null);

      // Create test user data similar to the sample provided
      const testUser = {
        username: "Stewie",
        email: "Stewie@my-yoo.com",
        full_name: "Stewie",
        password:
          "$2b$10$6mk2Mw0isJ3hKbK1pKyYUOA4qBNOpyyOXQi.uh5uXS52CkkkagvoG", // hashed password
        audio_sample:
          "https://s3.us-east-2.amazonaws.com/com.mkdlabs.images/baas/qualitysign/029888930111fritz-voice.mp3",
        interests: {
          music: "Hip Hop",
          musician: "Jay Z",
          sports: "Baseball",
          actor: "Brad Pitt",
          interest: "Acting",
          error: "",
        },
        avatar_image:
          "https://s3.us-east-2.amazonaws.com/com.mkdlabs.images/baas/qualitysign/024494199829zeeconvert-resizer.png",
        email_sent: true,
        simli_status: "completed",
        face_id: "99a87eca-e261-493b-81c4-02ea5586f23c",
        voice_id: "8bc1c0f2-3013-4b96-8acc-8182fa71fa86",
      };

      // Add user to the database
      const response = await fetch("http://localhost:5001/api/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(testUser),
      });

      if (!response.ok) {
        throw new Error(`Failed to add test user: ${response.status}`);
      }

      // Fetch updated list of users
      const userData = await fetchUsers();
      setUsers(userData);
    } catch (err) {
      console.error("Error adding test user:", err);
      setError(
        "Failed to add test user. The server may already have the user."
      );
    } finally {
      setIsAddingTestUser(false);
    }
  };

  // Start a chat session with the selected user
  const startChatSession = async (user) => {
    if (!user.face_id || !user.voice_id) {
      setError(
        `Cannot start chat with ${
          user.username || user.full_name || "this user"
        } - missing face or voice ID`
      );
      return;
    }

    setSelectedUser(user);
    setIsStartingSession(true);
    setError(null);

    try {
      // Prepare system prompt for the chat
      const systemPrompt =
        "You are a friendly AI avatar representing a real person in a chat. Keep conversations casual and engaging.";

      // Create opening message with user's name
      const userName = user.full_name || user.username || "there";
      const welcomeMessage = `Hey ${userName}, what's up?`;

      const payload = {
        apiKey: "n8sesdejo1tnudor5ljcg",
        faceId: user.face_id,
        voiceId: user.voice_id,
        ttsProvider: "Cartesia",
        ttsModel: "sonic-turbo-2025-03-07",
        ttsAPIKey: "sk_car_ANhQon9hRbqMDBgR5Z5n9Q",
        systemPrompt: systemPrompt,
        firstMessage: welcomeMessage,
        language: "en",
        maxSessionLength: 3600,
        maxIdleTime: 300,
      };

      const res = await fetch("https://api.simli.ai/startE2ESession", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`Session start failed: ${res.status} - ${errText}`);
      }

      const result = await res.json();

      if (result?.roomUrl) {
        setRoomUrl(result.roomUrl);
      } else {
        throw new Error("No room URL returned from session creation");
      }
    } catch (err) {
      setError("Failed to start chat session: " + err.message);
      console.error("Session error:", err);
    } finally {
      setIsStartingSession(false);
    }
  };

  // Clear error messages after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [error]);

  // End the chat session
  const endChatSession = () => {
    setRoomUrl(null);
    setSelectedUser(null);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <div className="flex-grow py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Yoo Chat</h1>
            <p className="mt-2 text-lg text-gray-600">
              Connect with other users through AI-powered avatars
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="p-6">
              {roomUrl ? (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-800">
                      Chatting with{" "}
                      {selectedUser?.username ||
                        selectedUser?.full_name ||
                        "User"}
                    </h2>
                    <button
                      onClick={endChatSession}
                      className="bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-md transition-colors"
                    >
                      End Chat
                    </button>
                  </div>
                  <div className="h-[600px] w-full rounded-lg overflow-hidden border border-gray-200">
                    <iframe
                      src={roomUrl}
                      title="Simli Avatar Chat Session"
                      className="w-full h-full"
                      allow="camera; microphone; fullscreen"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-800">
                    Select a user to chat with
                  </h2>

                  {isLoadingUsers ? (
                    <div className="flex flex-col items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
                      <p>Loading users...</p>
                    </div>
                  ) : isStartingSession ? (
                    <div className="flex flex-col items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
                      <p>
                        Starting chat with{" "}
                        {selectedUser?.username ||
                          selectedUser?.full_name ||
                          "User"}
                        ...
                      </p>
                    </div>
                  ) : (
                    <div>
                      {users.length === 0 ? (
                        <div className="text-center py-8">
                          <p className="text-gray-500 mb-4">
                            No users found in the database
                          </p>
                          <button
                            onClick={addTestUser}
                            disabled={isAddingTestUser}
                            className={`px-4 py-2 ${
                              isAddingTestUser
                                ? "bg-gray-400"
                                : "bg-blue-500 hover:bg-blue-600"
                            } text-white rounded-md transition-colors`}
                          >
                            {isAddingTestUser ? (
                              <>
                                <span className="inline-block animate-spin mr-2">
                                  ⟳
                                </span>
                                Adding Test User...
                              </>
                            ) : (
                              "Add Test User (Stewie)"
                            )}
                          </button>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                          {users.map((user) => (
                            <div
                              key={user._id}
                              className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors relative"
                              onClick={() => startChatSession(user)}
                              onMouseEnter={() => setHoverUser(user)}
                              onMouseLeave={() => setHoverUser(null)}
                            >
                              <img
                                src={
                                  user.avatar_image ||
                                  `https://ui-avatars.com/api/?name=${
                                    user.full_name || user.username || "User"
                                  }&background=random`
                                }
                                alt={user.full_name || user.username || "User"}
                                className="w-16 h-16 rounded-full mb-2 object-cover border border-gray-200"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = `https://ui-avatars.com/api/?name=${
                                    user.full_name || user.username || "User"
                                  }&background=random`;
                                }}
                              />
                              <span className="text-sm font-medium text-center">
                                {user.full_name || user.username || "User"}
                              </span>
                              <span className="text-xs text-gray-500 mt-1">
                                {user.face_id && user.voice_id
                                  ? "Ready"
                                  : "Missing IDs"}
                              </span>

                              {/* User info popup on hover */}
                              {hoverUser && hoverUser._id === user._id && (
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-56 bg-white shadow-lg rounded-md p-3 z-10 border border-gray-200">
                                  <div className="text-sm">
                                    <p className="font-semibold mb-1">
                                      {user.full_name || user.username}
                                    </p>
                                    <p className="text-gray-600 mb-1 text-xs">
                                      {user.email}
                                    </p>
                                    {user.interests && (
                                      <div className="text-xs text-gray-700 mt-2">
                                        <p>
                                          <span className="font-medium">
                                            Interests:
                                          </span>{" "}
                                          {user.interests.interest}
                                        </p>
                                        {user.interests.music && (
                                          <p>
                                            <span className="font-medium">
                                              Music:
                                            </span>{" "}
                                            {user.interests.music}
                                          </p>
                                        )}
                                        {user.interests.sports && (
                                          <p>
                                            <span className="font-medium">
                                              Sports:
                                            </span>{" "}
                                            {user.interests.sports}
                                          </p>
                                        )}
                                      </div>
                                    )}
                                    <div className="mt-2 pt-2 border-t border-gray-100 text-xs">
                                      <p className="truncate">
                                        <span className="font-medium">
                                          Face ID:
                                        </span>{" "}
                                        {user.face_id}
                                      </p>
                                      <p className="truncate">
                                        <span className="font-medium">
                                          Voice ID:
                                        </span>{" "}
                                        {user.voice_id}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-3 h-3 bg-white border-r border-b border-gray-200"></div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Navigation buttons */}
          <div className="mt-8 flex justify-between">
            <Link
              to="/"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Back to Studio
            </Link>
            <Link
              to="/yoo-create"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Go to Yoo Create
            </Link>
          </div>
        </div>
      </div>

      {/* Sticky footer group */}
      <div className="w-full mt-auto">
        {/* Billing Summary */}
        <BillingSummary />

        {/* Footer */}
        <footer className="bg-white py-4 border-t border-gray-200 text-center text-gray-600 text-sm">
          <p>© 2023 YOO! All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
}

export default YooChatPage;
